import React from 'react';
import InputTypeComponent from '../../../components/inputs/InputTypeComponent';
import { normalizeInputType } from '../../../utils/featureNormalization';
import { applyUserSelection } from '../../../utils/systemSelections';
import { DESIGN_MODULE_CATALOG, getFlowMode, getSelectedFeatureValueDisplayText, normalizeDesignSettings } from '../utils';
import { isOutputFeature, isSystemFeature, resolveCarouselFeature } from '../../../utils/valuePresentation';
import ImageCarousel from '../../../components/inputs/ImageCarousel';

const hasPreviewValue = (value) => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return value !== undefined && value !== null && value !== '';
};

const getPreviewFeatureInfo = (featureInfo, element) => {
    const renderType = normalizeInputType(element.renderType || featureInfo.inputType || element.inputType);
    return {
        ...featureInfo,
        inputType: renderType,
        valueSchema: featureInfo.valueSchema || featureInfo.property?.valueSchema || [],
        cardLayout: featureInfo.cardLayout || featureInfo.property?.cardLayout || null,
        dataBindings: element.dataBindings || {},
        property: {
            ...(featureInfo.property || {}),
            inputType: renderType.toLowerCase(),
            valueSchema: featureInfo.valueSchema || featureInfo.property?.valueSchema || [],
            cardLayout: featureInfo.cardLayout || featureInfo.property?.cardLayout || null
        }
    };
};

const getModuleByBlock = (blockName) => DESIGN_MODULE_CATALOG.find(module => module.blockId === blockName);

const getModuleConfig = (blockName, designSettings) => {
    const module = getModuleByBlock(blockName);
    if (!module) return null;
    return {
        module,
        config: normalizeDesignSettings(designSettings).modules[module.id]
    };
};

const moduleCardStyle = {
    border: '1px solid var(--border, #e5e7eb)',
    borderRadius: 'var(--module-radius, 10px)',
    background: 'var(--bg-card, #ffffff)',
    color: 'var(--text-primary, #1f2937)',
    padding: '1.25rem',
    boxShadow: '0 4px 10px rgba(15,23,42,0.06)'
};

const moduleTitleStyle = {
    margin: '0 0 0.75rem 0',
    fontSize: '0.95rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.04em'
};

const renderList = (items = []) => items.length ? items : ['Option A', 'Option B', 'Option C'];

const pageRequiredFilled = (page, features, selections) => {
    const requiredInputs = page?.elements?.filter(el => (el.type === 'feature' || el.type === 'variable') && !el.isOptional) || [];
    return requiredInputs.every(el => {
        const name = el.featureName || el.variableName;
        const isOutput = isOutputFeature(features.find(f => f.name === name));
        return isOutput || hasPreviewValue(selections[name]);
    });
};

const ProcessOverview = ({ pages, activeIndex }) => (
    <section style={{ ...moduleCardStyle, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {pages.map((page, index) => (
                <React.Fragment key={page.id || index}>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        fontWeight: 700,
                        color: index === activeIndex ? 'var(--text-primary)' : 'var(--text-secondary)'
                    }}>
                        <span style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: index === activeIndex ? 'var(--accent, #2563eb)' : 'var(--bg-secondary)',
                            color: index === activeIndex ? '#fff' : 'var(--text-secondary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem'
                        }}>{index + 1}</span>
                        {page.title || `Step ${index + 1}`}
                    </span>
                    {index < pages.length - 1 && <span style={{ color: 'var(--text-secondary)' }}>-</span>}
                </React.Fragment>
            ))}
        </div>
    </section>
);

export const IntroFrontPage = ({ designSettings, onNext }) => {
    const settings = normalizeDesignSettings(designSettings);
    const config = settings.modules.intro || {};
    const logo = settings.modules.logo || {};
    const variant = config.variant || 'welcome';
    const isHero = variant === 'hero';
    const isCompact = variant === 'compact';

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            width: '100%',
            maxWidth: isHero ? '720px' : '560px',
            margin: '0 auto',
            textAlign: 'center',
            padding: isHero ? '3rem 1.5rem' : isCompact ? '1.5rem 1rem' : '2.5rem 1.25rem',
            minHeight: isCompact ? '280px' : '420px'
        }}>
            {logo.enabled && (
                <div style={{ marginBottom: isCompact ? '1rem' : '1.5rem' }}>
                    {logo.logoUrl ? (
                        <img
                            src={logo.logoUrl}
                            alt={logo.logoName || 'Organization logo'}
                            style={{ display: 'block', maxWidth: isHero ? '220px' : '180px', maxHeight: isHero ? '72px' : '56px', objectFit: 'contain', margin: '0 auto' }}
                        />
                    ) : (
                        <div style={{ ...moduleCardStyle, minWidth: '120px', fontWeight: 800, padding: '0.85rem 1.25rem' }}>
                            {logo.text || 'Logo'}
                        </div>
                    )}
                </div>
            )}
            <h1 className="cfg-header" style={{
                margin: 0,
                fontSize: isHero ? '2rem' : isCompact ? '1.35rem' : '1.65rem',
                fontWeight: 800,
                lineHeight: 1.2
            }}>
                {config.title || 'Welcome'}
            </h1>
            <p className="cfg-description" style={{
                margin: isCompact ? '0.75rem 0 1.25rem' : '1rem 0 1.75rem',
                maxWidth: '40rem',
                fontSize: isHero ? '1.05rem' : '0.95rem',
                lineHeight: 1.6
            }}>
                {config.text || 'Start your configuration.'}
            </p>
            <button type="button" className="cfg-next-btn" onClick={onNext}>
                {config.buttonText || 'Start'} <i className="fa-solid fa-arrow-right"></i>
            </button>
        </div>
    );
};

/**
 * Renders preview blocks for the configurator preview
 * Used in both the PreviewModal and potentially other preview contexts
 */
export const PreviewBlockRenderer = ({
    blockName,
    pages,
    previewPageIndex,
    setPreviewPageIndex,
    previewSelections,
    setPreviewSelections,
    previewAvailableDomains = null,
    features,
    designSettings,
    introEnabled = false,
    onReturnToIntro
}) => {
    const flowMode = getFlowMode(designSettings);
    const flowConfig = normalizeDesignSettings(designSettings).modules[flowMode] || {};
    const isOptionDisabled = (varName, val) => {
        if (!previewAvailableDomains) return false;
        const allowed = previewAvailableDomains[varName];
        if (!Array.isArray(allowed)) return false;
        return !allowed.map(String).includes(String(val));
    };

    const renderPageBody = (page) => (
        <>
            {(page.elements || []).map(el => {
                if (el.type === 'header') return <h4 key={el.id} className="cfg-header">{el.text}</h4>;
                if (el.type === 'description') return <p key={el.id} className="cfg-description">{el.text}</p>;
                if (el.type === 'feature' || el.type === 'variable') {
                    const featName = el.featureName || el.variableName;
                    const featureInfo = features.find(v => v.name === featName) || { name: featName, values: [] };
                    const previewFeatureInfo = getPreviewFeatureInfo(featureInfo, el);
                    const isOutput = isOutputFeature(featureInfo);
                    const renderType = normalizeInputType(previewFeatureInfo.inputType);
                    if (isSystemFeature(featureInfo) && renderType !== 'ImageCarousel') {
                        return null;
                    }
                    return (
                        <div key={el.id} className="cfg-feature-group">
                            <label className="cfg-feature-label">
                                <span>
                                    {featureInfo.displayText || featureInfo.name}
                                    {!isOutput && !el.isOptional && <span style={{ color: 'var(--error)', marginLeft: '0.25rem' }}>*</span>}
                                </span>
                                {isOutput && <span style={{ fontSize: '0.7rem', padding: '0.1rem 0.35rem', background: 'rgba(220,38,38,0.1)', color: 'var(--error)', borderRadius: '4px', fontWeight: 600 }}>Output</span>}
                            </label>
                            {isOutput ? (
                                <input type="text" className="cfg-input-control cfg-select-input cfg-no-select-chrome" value="(Calculated Value)" disabled />
                            ) : (
                                <InputTypeComponent
                                    feature={previewFeatureInfo}
                                    value={previewSelections[featName] ?? ''}
                                    onChange={e => setPreviewSelections(prev => applyUserSelection(prev, featName, e.target.value))}
                                    isOptionDisabled={(val) => isOptionDisabled(featName, val)}
                                    className="cfg-input-control cfg-select-input cfg-no-select-chrome"
                                />
                            )}
                        </div>
                    );
                }
                return null;
            })}
        </>
    );

    const renderPageHeader = (page) => (
        <>
            {page.title && <h3 className="cfg-header">{page.title}</h3>}
            {page.description && <p className="cfg-description">{page.description}</p>}
        </>
    );

    switch (blockName) {
        case 'intro-panel':
            return null;
        case 'logo-display': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const config = moduleData?.config || {};
            const align = config.placement === 'top-right' ? 'flex-end' : config.placement === 'top' || config.placement === 'bottom' ? 'center' : 'flex-start';
            return (
                <section key="logo-display" style={{ display: 'flex', justifyContent: align }}>
                    <div style={{ ...moduleCardStyle, minWidth: '120px', textAlign: 'center', fontWeight: 800, padding: '0.85rem 1.25rem' }}>
                        {config.logoUrl ? (
                            <img
                                src={config.logoUrl}
                                alt={config.logoName || 'Organization logo'}
                                style={{ display: 'block', maxWidth: '180px', maxHeight: '56px', objectFit: 'contain' }}
                            />
                        ) : (
                            config.text || 'Logo'
                        )}
                    </div>
                </section>
            );
        }
        case 'pnc-catalog': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const config = moduleData?.config || {};
            return (
                <section key="pnc-catalog" style={moduleCardStyle}>
                    <h4 style={moduleTitleStyle}>{config.title || 'PNC Catalog'}</h4>
                    <p className="cfg-description" style={{ margin: 0 }}>
                        Catalog-driven search, product presentation, filtering, and comparison modules.
                    </p>
                </section>
            );
        }
        case 'pnc-search': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const config = moduleData?.config || {};
            return (
                <section key="pnc-search" style={moduleCardStyle}>
                    <h4 style={moduleTitleStyle}>PNC Search</h4>
                    <input className="cfg-input-control cfg-select-input cfg-no-select-chrome" value="" placeholder={config.placeholder || 'Search PNC...'} readOnly />
                </section>
            );
        }
        case 'product-type-cards': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const config = moduleData?.config || {};
            return (
                <section key="product-type-cards" style={moduleCardStyle}>
                    <h4 style={moduleTitleStyle}>Product Type</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem' }}>
                        {renderList(config.items).map(item => (
                            <button key={item} type="button" className="cfg-back-btn" style={{ justifyContent: 'center', padding: '0.85rem' }}>{item}</button>
                        ))}
                    </div>
                </section>
            );
        }
        case 'needs-configurator': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const config = moduleData?.config || {};
            return (
                <section key="needs-configurator" style={moduleCardStyle}>
                    <h4 style={moduleTitleStyle}>{config.title || 'Find the right solution'}</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {['Budget', 'Capacity', 'Installation'].map(item => (
                            <button key={item} type="button" className="cfg-back-btn" style={{ padding: '0.6rem 0.8rem' }}>{item}</button>
                        ))}
                    </div>
                </section>
            );
        }
        case 'filter-panel': {
            return (
                <section key="filter-panel" style={moduleCardStyle}>
                    <h4 style={moduleTitleStyle}>Filter</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {['In stock', 'Energy class', 'Series'].map(item => (
                            <span key={item} style={{ border: '1px solid var(--border)', borderRadius: '999px', padding: '0.4rem 0.65rem', fontSize: '0.8rem' }}>{item}</span>
                        ))}
                    </div>
                </section>
            );
        }
        case 'compare-panel': {
            return (
                <section key="compare-panel" className="cfg-overview-card">
                    <h4 style={moduleTitleStyle}>Compare</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.85rem' }}>
                        <div>Selected product A</div>
                        <div>Selected product B</div>
                    </div>
                </section>
            );
        }
        case 'image-carousel': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const bound = resolveCarouselFeature(features, moduleData?.config?.bindFeature);
            if (!bound) {
                return (
                    <section key="image-carousel" style={moduleCardStyle}>
                        <h4 style={moduleTitleStyle}>Image carousel</h4>
                        <p className="cfg-description" style={{ margin: 0 }}>Bind this block to a visual feature, or mark a feature as a hidden system carousel.</p>
                    </section>
                );
            }
            return (
                <section key="image-carousel">
                    <ImageCarousel
                        feature={bound}
                        value={previewSelections[bound.name] ?? ''}
                        onChange={e => setPreviewSelections(prev => applyUserSelection(prev, bound.name, e.target.value))}
                        isOptionDisabled={(val) => isOptionDisabled(bound.name, val)}
                    />
                </section>
            );
        }
        case 'accordion-sections':
        case 'tab-sections':
        case 'process-line':
        case 'all-in-one-panel':
            return null;
        case 'features-view': {
            const safePages = pages.length ? pages : [{ id: 'page-1', title: '', description: '', elements: [] }];
            const activePage = safePages[previewPageIndex] || safePages[0];

            if (flowMode === 'allInOne') {
                return (
                    <div key="features-view" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {safePages.map(page => (
                            <section key={page.id}>
                                {renderPageHeader(page)}
                                {renderPageBody(page)}
                            </section>
                        ))}
                    </div>
                );
            }

            if (flowMode === 'accordions') {
                return (
                    <div key="features-view" style={{ display: 'flex', flexDirection: 'column' }}>
                        {safePages.map((page, index) => (
                            <details key={page.id} open={index === 0} style={{ borderTop: '1px solid var(--border)', padding: flowConfig.variant === 'compact' ? '0.5rem 0' : '0.75rem 0' }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 700 }}>{page.title || `Section ${index + 1}`}</summary>
                                <div style={{ marginTop: '0.75rem' }}>
                                    {page.description && <p className="cfg-description">{page.description}</p>}
                                    {renderPageBody(page)}
                                </div>
                            </details>
                        ))}
                    </div>
                );
            }

            if (flowMode === 'tabs') {
                const isSide = flowConfig.variant === 'side';
                return (
                    <div key="features-view" style={{ display: 'flex', flexDirection: isSide ? 'row' : 'column', gap: '1rem', alignItems: isSide ? 'flex-start' : 'stretch' }}>
                        <div style={{ display: 'flex', flexDirection: isSide ? 'column' : 'row', gap: '0.5rem', flexWrap: isSide ? 'nowrap' : 'wrap' }}>
                            {safePages.map((page, index) => (
                                <button
                                    key={page.id}
                                    type="button"
                                    className={index === previewPageIndex ? 'cfg-next-btn' : 'cfg-back-btn'}
                                    style={{ padding: '0.55rem 0.85rem' }}
                                    onClick={() => setPreviewPageIndex(index)}
                                >
                                    {page.title || `Tab ${index + 1}`}
                                </button>
                            ))}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {renderPageHeader(activePage)}
                            {renderPageBody(activePage)}
                        </div>
                    </div>
                );
            }

            return (
                <div key="features-view" style={{ display: 'flex', flexDirection: 'column' }}>
                    <ProcessOverview pages={safePages} activeIndex={previewPageIndex} />
                    {renderPageHeader(activePage)}
                    {renderPageBody(activePage)}
                </div>
            );
        }
        case 'selections-overview': {
            return (
                <div key="selections-overview" className="cfg-overview-card">
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
                        Your Selections
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.85rem' }}>
                        {Object.keys(previewSelections).length === 0 && <span style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>No features selected yet.</span>}
                        {Object.entries(previewSelections).filter(([k]) => {
                            const matched = features.find(f => f.name === k);
                            return !isSystemFeature(matched);
                        }).map(([k, v]) => {
                            const matched = features.find(f => f.name === k);
                            return (
                                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: '0.4rem' }}>
                                    <span style={{ fontWeight: 500 }}>{matched ? (matched.displayText || matched.name) : k}</span>
                                    <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>{getSelectedFeatureValueDisplayText(matched, v)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        case 'wizard-navigation': {
            const safePages = pages.length ? pages : [{ elements: [] }];
            const activePage = safePages[previewPageIndex] || safePages[0];
            const isProcess = flowMode === 'processLine';
            const isPageValid = isProcess
                ? pageRequiredFilled(activePage, features, previewSelections)
                : safePages.every(page => pageRequiredFilled(page, features, previewSelections));
            const canReturnToIntro = introEnabled && typeof onReturnToIntro === 'function';
            const showPrevPage = isProcess && previewPageIndex > 0;
            const showNextPage = isProcess && previewPageIndex < safePages.length - 1;

            return (
                <div key="wizard-navigation" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                    {(showPrevPage || canReturnToIntro) && (
                        <button 
                            className="cfg-back-btn" 
                            onClick={() => {
                                if (showPrevPage) {
                                    setPreviewPageIndex(previewPageIndex - 1);
                                    return;
                                }
                                onReturnToIntro();
                            }}
                        >
                            <i className="fa-solid fa-arrow-left"></i> Back
                        </button>
                    )}
                    {showNextPage ? (
                        <button 
                            className="cfg-next-btn"
                            style={{ marginLeft: 'auto', opacity: isPageValid ? 1 : 0.5, cursor: isPageValid ? 'pointer' : 'not-allowed' }}
                            disabled={!isPageValid}
                            onClick={() => setPreviewPageIndex(previewPageIndex + 1)}
                        >
                            Next <i className="fa-solid fa-arrow-right"></i>
                        </button>
                    ) : (
                        <button 
                            className="cfg-next-btn"
                            style={{ marginLeft: 'auto', opacity: isPageValid ? 1 : 0.5, cursor: isPageValid ? 'pointer' : 'not-allowed' }}
                            disabled={!isPageValid}
                            onClick={() => alert('Configuration completed in preview!')}
                        >
                            Submit Specs <i className="fa-solid fa-check"></i>
                        </button>
                    )}
                </div>
            );
        }
        case 'exit-action': {
            const exitEl = pages.flatMap(p => p.elements).find(el => el.type === 'exit-button');
            if (!exitEl) return null;
            return (
                <button 
                    key="exit-action" 
                    className="cfg-back-btn"
                    style={{ alignSelf: 'flex-start', marginTop: '1rem' }}
                    onClick={() => alert('Exit triggered.')}
                >
                    <i className="fa-solid fa-arrow-right-from-bracket"></i>
                    {exitEl.text || 'Exit Configurator'}
                </button>
            );
        }
        case 'send-email': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const config = moduleData?.config || {};
            return (
                <section key="send-email" className="cfg-overview-card">
                    <h4 style={moduleTitleStyle}>Send configuration</h4>
                    {config.infoText && <p className="cfg-description">{config.infoText}</p>}
                    <button type="button" className="cfg-next-btn" onClick={() => alert('Email action triggered.')}>
                        <i className="fa-solid fa-envelope"></i>
                        {config.buttonText || 'Send email'}
                    </button>
                </section>
            );
        }
        case 'show-pnc': {
            const moduleData = getModuleConfig(blockName, designSettings);
            const config = moduleData?.config || {};
            return (
                <section key="show-pnc" className="cfg-overview-card">
                    <h4 style={moduleTitleStyle}>{config.label || 'PNC for configuration'}</h4>
                    <div style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: '0.06em' }}>PNC-000000</div>
                </section>
            );
        }
        default:
            return null;
    }
};

export default PreviewBlockRenderer;
